import React, {Children, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Breadcrumb as ChakraBreadcrumb,
    Button,
    Flex,
    useMultiStyleConfig
} from '@chakra-ui/react'
import {BreadcrumbSeparatorIcon} from '../icons'

const Breadcrumb = ({children, ...props}) => {
    const styles = useMultiStyleConfig('Breadcrumb', props)

    const breadcrumbListRef = useRef()
    const [isOverflowing, setIsOverflowing] = useState(false)

    useEffect(() => {
        let timeout
        function debounce(callback, delay) {
            return function (...args) {
                clearTimeout(timeout)
                timeout = setTimeout(() => {
                    callback(...args)
                }, delay)
            }
        }

        const checkOverflow = () => {
            if (breadcrumbListRef.current) {
                setIsOverflowing(
                    breadcrumbListRef.current.scrollWidth > breadcrumbListRef.current.clientWidth
                )
            }
        }
        checkOverflow()

        const debouncedCheckOverflow = debounce(checkOverflow, 200)

        window.addEventListener('resize', debouncedCheckOverflow, {passive: true})

        return () => {
            clearTimeout(timeout)
            window.removeEventListener('resize', debouncedCheckOverflow)
        }
    }, [])

    return (
        <Box
            ref={breadcrumbListRef}
            width="100vw"
            maxWidth="min(100%, 1536px)"
            margin={{base: 0, '2xl': 'auto'}}
            overflowX="auto"
            sx={{
                '&::-webkit-scrollbar': {
                    display: 'none'
                },
                'scrollbar-width': 'none'
            }}
        >
            {isOverflowing && (
                <Button
                    onClick={() => {
                        setIsOverflowing(false)
                    }}
                    cursor="pointer"
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    height={14}
                    sx={{
                        '&::after': {
                            content: '"…"',
                            userSelect: 'none'
                        }
                    }}
                    variant="unstyled"
                ></Button>
            )}
            {isOverflowing && (
                <Box
                    position="absolute"
                    right="0"
                    height={14}
                    width="2.125rem"
                    bgGradient="linear(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 88%)"
                ></Box>
            )}
            <Flex {...styles.wrapper}>
                {isOverflowing && (
                    <BreadcrumbSeparatorIcon
                        {...styles.separatorIcon}
                        {...(isOverflowing
                            ? {
                                  margin: '0 0.75rem 0 1.5rem'
                              }
                            : {
                                  margin: '0 0.75rem'
                              })}
                    />
                )}
                <ChakraBreadcrumb separator={<BreadcrumbSeparatorIcon {...styles.separatorIcon} />}>
                    {Children.map(children, (child, index) => {
                        if (isOverflowing && index < Children.count(children) - 1) {
                            return React.cloneElement(child, {
                                opacity: 0,
                                padding: 0,
                                maxWidth: 0
                            })
                        } else {
                            return React.cloneElement(child, {
                                opacity: 1,
                                maxWidth: '100%',
                                transition: 'max-width 1s linear, opacity 1.5s ease'
                            })
                        }
                    })}
                </ChakraBreadcrumb>
            </Flex>
        </Box>
    )
}

Breadcrumb.propTypes = {
    children: PropTypes.node
}

export default Breadcrumb
